import React from 'react';
import { Content } from '../components';
import { useGetAllClinics, useAddClinic, useDeleteClinic, useToggleClinic, useResetPassword } from '../hooks/clinic';
import { IAddClinicReq, IClinic } from '../hooks/clinic/index.type';
import { unMasking, validateEmail, validatePassword, validatePhone } from '../utils/validators';
import { CustomTableRow, Table, TableBody, TableButtons, TableHead, TableImageRow, TableNoContent, TableRowTitle, TableStatusWithToggle } from '../components/Table';
import { clinicTableHeadData } from '../hooks/clinic/staticData';
import TableSkeleton from '../Skeletons/TableSkeleton';
import Pagination from '../components/Pagination';
import { DeleteModal, AddEditModal, ToggleModal, ResetPasswordModal } from '../pageComponents/Clinics';
import { AddEditModalState, IaddEditModalStates } from '../pageComponents/Clinics/AddEditModal';
import { DeleteModalState, IdeleteModalStates } from '../pageComponents/Clinics/DeleteModal';
import { IresetPasswordModalStates } from '../pageComponents/Clinics/ResetPasswordModal';
import { useNavigate } from 'react-router-dom';

const Clinics = () => {
  const navigate=useNavigate();
  const [pageNumber, setPageNumber] = React.useState<number>(1);
  const perpageData = 15;

  // ====================================== Add edit states start ======================================
  const [addEditModal, setAddEditModal] = React.useState<AddEditModalState>({
    status: false,
  });
  const startaddEditModalStatesValue = {
    name: { value: '', error: '' },
    email: { value: '', error: '' },
    phone: { value: '', error: '' },
    address: { value: '', error: '' },
    password: { value: '', error: '' },
    modules: {
      value: {
        fileSharing: false,
        checkIn: false,
        questionnaire: false,
      },
      error: '',
    },
  };
  const [addEditModalStates, setAddEditModalStates] = React.useState<IaddEditModalStates>(startaddEditModalStatesValue);
  // ====================================== Add edit states end ======================================

  // ====================================== Toggle states start ======================================
  const [toggleModal, setToggleModal] = React.useState<DeleteModalState>({
    status: false,
  });
  // ====================================== Toggle states end ======================================

  // ====================================== Reset Password States start ======================================
  const [resetPasswordModal, setResetPasswordModal] = React.useState<DeleteModalState>({
    status: false,
  });

  const startResetPasswordModalStatesValue = {
    password: { value: '', error: '' },
    passwordConfirm: { value: '', error: '' },
  };
  const [resetPasswordModalStates, setResetPasswordModalStates] = React.useState<IresetPasswordModalStates>(startResetPasswordModalStatesValue);
  // ====================================== Reset Password States end ======================================

  // ====================================== Delete states start ======================================
  const [deleteModal, setDeleteModal] = React.useState<DeleteModalState>({
    status: false,
  });

  const startDeleteModalStatesValue = {
    email: { value: '', error: '' },
  };
  const [deleteModalStates, setDeleteModalStates] = React.useState<IdeleteModalStates>(startDeleteModalStatesValue);
  // ====================================== Delete states end ======================================

  // ====================================== Hooks ======================================
  const { clinicsData, getClinicsData, clinicsDataLoading } = useGetAllClinics({ pageNumber, perpageData });
  const { addClinicLoading, addClinic } = useAddClinic({ clinicId: addEditModal.data?._id });
  const { deleteClinicLoading, deleteClinic } = useDeleteClinic({ clinicId: deleteModal.data?._id });
  const { toggleClinicLoading, toggleClinic } = useToggleClinic({ clinicId: toggleModal.data?._id });
  const { resetPasswordLoading, resetPassword } = useResetPassword({ clinicId: resetPasswordModal.data?._id });

  // ====================================== Add edit functions start ======================================
  React.useEffect(() => {
    if (addEditModal.data?._id) {
      setAddEditModalStates({
        name: { value: addEditModal.data.name, error: '' },
        email: { value: addEditModal.data.email, error: '' },
        phone: { value: addEditModal.data.phone, error: '' },
        address: { value: addEditModal.data.address, error: '' },
        password: { value: '', error: '' },
        modules: {
          value: {
            fileSharing: addEditModal.data.modules && addEditModal.data.modules.fileSharing ? addEditModal.data.modules.fileSharing : false,
            checkIn: addEditModal.data.modules && addEditModal.data.modules.checkIn ? addEditModal.data.modules.checkIn : false,
            questionnaire: addEditModal.data.modules && addEditModal.data.modules.questionnaire ? addEditModal.data.modules.questionnaire : false,
          },
          error: '',
        },
      });
    }
  }, [addEditModal]);

  const onPageChange = (currentPage: { selected: number }) => {
    setPageNumber(currentPage.selected + 1);
  };

  const handleCloseModal = () => {
    setAddEditModal({ status: false });
    setAddEditModalStates(startaddEditModalStatesValue);
  };

  const handleAddClinic = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!addEditModalStates.name.value.trim()) {
      setAddEditModalStates(prevVal => ({ ...prevVal, name: { ...prevVal.name, error: 'Please enter clinic name' } }));
    } else if (!addEditModalStates.email.value.trim() || !validateEmail(addEditModalStates.email.value)) {
      setAddEditModalStates(prevVal => ({ ...prevVal, email: { ...prevVal.email, error: 'Please enter valid clinic email' } }));
    } else if (!addEditModalStates.phone.value.trim() || !validatePhone(unMasking(addEditModalStates.phone.value))) {
      setAddEditModalStates(prevVal => ({ ...prevVal, phone: { ...prevVal.phone, error: 'Please enter valid clinic phone' } }));
    } else if (!addEditModalStates.address.value.trim()) {
      setAddEditModalStates(prevVal => ({ ...prevVal, address: { ...prevVal.address, error: 'Please enter clinic address' } }));
    } else {
      const formData: IAddClinicReq = {
        name: addEditModalStates.name.value,
        email: addEditModalStates.email.value,
        phone: unMasking(addEditModalStates.phone.value),
        address: addEditModalStates.address.value,
        modules: addEditModalStates.modules.value,
      };

      if (!addEditModal.data?._id) {
        if (addEditModalStates.password.value === '' || addEditModalStates.password.value.trim() === '') {
          setAddEditModalStates(prevVal => ({ ...prevVal, password: { ...prevVal.password, error: 'Please set clinic password' } }));
        } else {
          formData.password = addEditModalStates.password.value;
          formData.passwordConfirm = addEditModalStates.password.value;
        }
      }

      const data = await addClinic(formData);
      if (data?.status === 'success') {
        handleCloseModal();
        getClinicsData();
      }
    }
  };
  // ====================================== Add edit functions end ======================================

  // ====================================== Toggle functions start ======================================
  const handleToggleClinic = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (toggleModal.data) {
      const data = await toggleClinic({ active: !toggleModal.data.active });
      if (data?.status === 'success') {
        setToggleModal({ status: false });
        getClinicsData();
      }
    }
  };
  // ====================================== Toggle functions end ======================================

  // ====================================== Reset Password functions start ======================================
  const handleCloseResetPasswordModal = () => {
    setResetPasswordModal({ status: false });
    setResetPasswordModalStates(startResetPasswordModalStatesValue);
  };

  const handleResetPassword = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (resetPasswordModal.data) {
      if (!resetPasswordModalStates.password.value.trim() || !validatePassword(resetPasswordModalStates.password.value)) {
        setResetPasswordModalStates(prevVal => ({ ...prevVal, password: { ...prevVal.password, error: 'Please enter strong password' } }));
      } else if (resetPasswordModalStates.passwordConfirm.value !== resetPasswordModalStates.password.value) {
        setResetPasswordModalStates(prevVal => ({ ...prevVal, passwordConfirm: { ...prevVal.passwordConfirm, error: 'Passwords do not match' } }));
      } else {
        const data = await resetPassword({ password: resetPasswordModalStates.password.value });
        if (data?.status === 'success') {
          handleCloseResetPasswordModal();
          getClinicsData();
        }
      }
    } else {
      setDeleteModalStates(prevVal => ({ ...prevVal, email: { ...prevVal.email, error: 'Please select clinic to delete' } }));
    }
  };
  // ====================================== Reset Password functions end ======================================

  // ====================================== Delete functions start ======================================
  const handleCloseDeleteModal = () => {
    setDeleteModal({ status: false });
    setDeleteModalStates(startDeleteModalStatesValue);
  };

  const handleDeleteClinic = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (deleteModal.data) {
      if (!deleteModalStates.email.value.trim() || deleteModalStates.email.value !== deleteModal.data?.email) {
        setDeleteModalStates(prevVal => ({ ...prevVal, email: { ...prevVal.email, error: 'Please enter valid email' } }));
      } else {
        await deleteClinic();
        handleCloseDeleteModal();
        getClinicsData();
      }
    } else {
      setDeleteModalStates(prevVal => ({ ...prevVal, email: { ...prevVal.email, error: 'Please select clinic to delete' } }));
    }
  };
  // ====================================== Delete functions end ======================================

  return (
    <Content
      currentMenu='clinics'
      headerTitle='Clinics'
      addBtn={true}
      addBtnClick={() => setAddEditModal({ status: true })}
      addBtnTitle='Add Clinic'
      searchBox={true}
      searchPlaceholder='Search Clinics'
      totalReult={clinicsData && clinicsData.totalResults > 0 ? clinicsData.totalResults + ' clinics' : ''}
    >
      <Table>
        <TableHead data={clinicTableHeadData} />
        <TableBody>
          <CustomTableRow>
            {clinicsDataLoading ? (
              <TableSkeleton rows={7} columns={10} />
            ) : clinicsData && clinicsData.totalResults > 0 ? (
              clinicsData.data.data.map((clinic: IClinic) => (
                <tr key={clinic._id}>
                  <TableImageRow alt={clinic.name} />
                  <TableRowTitle>{clinic.name}</TableRowTitle>
                  <td>{clinic.email}</td>
                  <td>{clinic.phone}</td>
                  <td>{clinic.address ? (clinic.address.length <= 40 ? clinic.address : clinic.address.slice(0, 40) + '...') : ''}</td>
                  <td>{clinic.clinicID ? clinic.clinicID : ''}</td>
                  <td>
                    <TableStatusWithToggle id={clinic._id} checked={clinic.active} onChange={() => setToggleModal({ status: true, data: clinic })} />
                  </td>
                  <TableButtons
                    buttons={[
                      {
                        icon:"associate-clinic",
                        tooltip:"Associate Clinic",
                        onClick: () => {
                         navigate(`/associate-clinic/${clinic._id}`);
                        }
                      },
                      { icon: 'lock', tooltip: 'Reset Password', onClick: () => setResetPasswordModal({ status: true, data: clinic }) },
                      { icon: 'edit-3', tooltip: 'Edit Clinic', onClick: () => setAddEditModal({ status: true, data: clinic }) },
                      { icon: 'trash', tooltip: 'Delete Clinic', variant: 'danger', onClick: () => setDeleteModal({ status: true, data: clinic }) },
                    ]}
                  />
                </tr>
              ))
            ) : (
              <TableNoContent colSpan={clinicTableHeadData.length} message='No data found' />
            )}
          </CustomTableRow>
        </TableBody>
      </Table>

      <Pagination itemsPerPage={perpageData} totalItems={clinicsData ? clinicsData.totalResults : 0} onPageChange={onPageChange} />

      {addEditModal.status && (
        <AddEditModal
          loading={addClinicLoading}
          addEditModal={addEditModal}
          onCloseModal={handleCloseModal}
          addEditModalStates={addEditModalStates}
          setAddEditModalStates={setAddEditModalStates}
          handleAddEdit={handleAddClinic}
        />
      )}

      {toggleModal.status && (
        <ToggleModal loading={toggleClinicLoading} toggleModal={toggleModal} onCloseModal={() => setToggleModal({ status: false })} handleToggle={handleToggleClinic} />
      )}

      {resetPasswordModal.status && (
        <ResetPasswordModal
          loading={resetPasswordLoading}
          resetPasswordModal={resetPasswordModal}
          resetPasswordModalStates={resetPasswordModalStates}
          setResetPasswordModalStates={setResetPasswordModalStates}
          onCloseModal={handleCloseResetPasswordModal}
          handleResetPassword={handleResetPassword}
        />
      )}

      {deleteModal.status && (
        <DeleteModal
          loading={deleteClinicLoading}
          deleteModal={deleteModal}
          deleteModalStates={deleteModalStates}
          setDeleteModalStates={setDeleteModalStates}
          onCloseModal={handleCloseDeleteModal}
          handleDelete={handleDeleteClinic}
        />
      )}
    </Content>
  );
};

export default Clinics;
